import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { customerFilter } from '@/hooks/useScheduledCallbacks';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/api-client';
import { VFTimelineEvent } from '@/components/vf';
import { CheckCircle2, Clock, Calendar, CalendarCheck } from 'lucide-react';

type EventStatus = 'confirmed' | 'pending' | 'completed' | 'no_show' | 'declined';
type EventType = 'meeting' | 'task' | 'call' | 'email' | 'followup';

interface MeetingShape {
  id?: string;
  meeting_date?: string | Date | null;
  scheduledAt?: string | Date | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  type?: string | null;
  duration_minutes?: number | null;
  status?: string | null;
}

interface CallShape {
  id?: string;
  follow_up_at?: string | Date | null;
  prospect_name?: string | null;
  prospect_company?: string | null;
  call_notes?: string | null;
  call_outcome?: string | null;
}

interface AgendaEvent {
  id: string;
  hour: number;
  time: string;
  title: string;
  subtitle: string;
  tag: string;
  status: EventStatus;
  type: EventType;
  raw: MeetingShape | CallShape;
}

interface ApiShape {
  entities: {
    ClientMeeting: { filter(query: Record<string, unknown>): Promise<MeetingShape[] | { data?: MeetingShape[] }> };
    CallLog: { filter(query: Record<string, unknown>, sort: string, limit: number): Promise<CallShape[] | { data?: CallShape[] }> };
  };
}

interface TaskAgendaVFProps {
  clientEmail?: string;
  /** Active customer (ADR-024): only its meetings and calls. */
  customerId?: string | null;
  onCall?: (raw: MeetingShape | CallShape) => void;
}

function hourLabel(n: number) {
  return `${String(n).padStart(2, '0')}:00`;
}

function isToday(dateStr: string | Date | null | undefined) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function isTomorrow(dateStr: string | Date | null | undefined) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth() && d.getDate() === t.getDate();
}

function isThisWeek(dateStr: string | Date | null | undefined) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const end = new Date(now);
  end.setDate(end.getDate() + (7 - now.getDay()));
  return d >= now && d <= end;
}

export default function TaskAgendaVF({ clientEmail, customerId = null, onCall }: TaskAgendaVFProps) {
  const { t } = useTranslation(['dialer', 'common']);
  const { data: meetings = [] } = useQuery<MeetingShape[]>({
    queryKey: ['agenda-meetings', clientEmail, customerId],
    queryFn: async () => {
      if (!clientEmail) return [];
      const result = await (api as unknown as ApiShape).entities.ClientMeeting.filter(customerFilter(customerId));
      return Array.isArray(result) ? result : (result?.data ?? []);
    },
    enabled: !!clientEmail,
  });

  const { data: allCalls = [] } = useQuery<CallShape[]>({
    queryKey: ['agenda-calls', clientEmail, customerId],
    queryFn: async () => {
      if (!clientEmail) return [];
      const result = await (api as unknown as ApiShape).entities.CallLog.filter(
        customerFilter(customerId),
        '-created_date',
        100,
      );
      return Array.isArray(result) ? result : (result?.data ?? []);
    },
    enabled: !!clientEmail,
  });

  const dayEvents = useMemo<AgendaEvent[]>(() => {
    const events: AgendaEvent[] = [];
    (meetings || []).forEach((m) => {
      const mdate = m.meeting_date || m.scheduledAt;
      if (!mdate || !isToday(mdate)) return;
      const d = new Date(mdate);
      const status: EventStatus =
        m.status === 'confirmed' ? 'confirmed' : m.status === 'pending' ? 'pending' : m.status === 'no_show' ? 'no_show' : 'pending';
      events.push({
        id: `m-${m.id}`,
        hour: d.getHours(),
        time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
        title: m.prospect_name || t('dialer:tasks.bookingTab.prospectFallback'),
        subtitle: m.prospect_company || '',
        tag: `${m.type || 'RDV'} · ${m.duration_minutes || 30}min`,
        status,
        type: 'meeting',
        raw: m,
      });
    });
    (allCalls || []).forEach((c) => {
      if (!c.follow_up_at || !isToday(c.follow_up_at)) return;
      const d = new Date(c.follow_up_at);
      events.push({
        id: `f-${c.id}`,
        hour: d.getHours(),
        time: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
        title: `Follow-up ${c.prospect_name || ''}`.trim(),
        subtitle: c.prospect_company || '',
        tag: c.call_notes?.slice(0, 60) || '',
        status: 'pending',
        type: c.call_outcome === 'callback_requested' ? 'call' : 'followup',
        raw: c,
      });
    });
    events.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    return events;
  }, [meetings, allCalls]);

  const resume = useMemo(() => {
    const todayMeetings = (meetings || []).filter((m) => isToday(m.meeting_date || m.scheduledAt));
    return {
      rdvs: todayMeetings.length,
      confirmed: todayMeetings.filter((m) => m.status === 'confirmed').length,
      pending: todayMeetings.filter((m) => m.status === 'pending').length,
      tasks: (allCalls || []).filter((c) => c.follow_up_at && isToday(c.follow_up_at)).length,
    };
  }, [meetings, allCalls]);

  const upcoming = useMemo(() => {
    const tomorrowMeetings = (meetings || []).filter((m) => isTomorrow(m.meeting_date || m.scheduledAt)).length;
    const tomorrowTasks = (allCalls || []).filter((c) => isTomorrow(c.follow_up_at)).length;
    const weekMeetings = (meetings || []).filter((m) => {
      const d = m.meeting_date || m.scheduledAt;
      return d && isThisWeek(d) && !isTomorrow(d) && !isToday(d);
    }).length;
    const weekTasks = (allCalls || []).filter(
      (c) => c.follow_up_at && isThisWeek(c.follow_up_at) && !isTomorrow(c.follow_up_at) && !isToday(c.follow_up_at),
    ).length;
    return { tomorrowMeetings, tomorrowTasks, weekMeetings, weekTasks };
  }, [meetings, allCalls]);

  const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <h3 className="text-[15px] font-semibold mb-3" style={{ color: 'var(--vf-text-1)' }}>
          {t('dialer:tasks.dayPlanning')}
        </h3>
        <div className="space-y-2">
          {hours.map((h) => {
            const eventsAtHour = dayEvents.filter((e) => e.hour === h);
            if (eventsAtHour.length === 0) {
              return (
                <div key={h} className="flex gap-3 items-center" style={{ minHeight: 36 }}>
                  <div className="w-14 shrink-0">
                    <span
                      className="vf-font-mono text-[12px] tabular-nums"
                      style={{ color: 'var(--vf-text-3)' }}
                    >
                      {hourLabel(h)}
                    </span>
                  </div>
                  <div className="flex-1 border-t" style={{ borderColor: 'var(--vf-border)' }} />
                </div>
              );
            }
            return eventsAtHour.map((e) => (
              <VFTimelineEvent
                key={e.id}
                time={e.time}
                title={e.title}
                subtitle={e.subtitle}
                tag={e.tag}
                status={e.status}
                type={e.type}
                onClick={() => onCall?.(e.raw)}
                actions={
                  e.status === 'pending' && e.type === 'meeting'
                    ? [{ label: t('dialer:tasks.bookingTab.followUp'), onClick: () => onCall?.(e.raw) }]
                    : []
                }
              />
            ));
          })}
          {dayEvents.length === 0 && (
            <div
              className="rounded-vf-lg border p-6 text-center text-sm"
              style={{
                background: 'var(--vf-surface-2)',
                borderColor: 'var(--vf-border)',
                color: 'var(--vf-text-3)',
              }}
            >
              {t('dialer:tasks.noEvents')}
            </div>
          )}
        </div>
      </div>

      <aside className="space-y-4">
        <div>
          <h3 className="text-[15px] font-semibold mb-3" style={{ color: 'var(--vf-text-1)' }}>
            {t('dialer:tasks.dayResume')}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <ResumeTile icon={<Calendar size={14} />} value={resume.rdvs} label={t('dialer:tasks.rdvs')} color="var(--vf-text-1)" />
            <ResumeTile
              icon={<CheckCircle2 size={14} />}
              value={resume.confirmed}
              label={t('dialer:tasks.confirmed')}
              color="var(--vf-green)"
            />
            <ResumeTile icon={<Clock size={14} />} value={resume.pending} label={t('dialer:tasks.pendingTasks')} color="var(--vf-yellow)" />
            <ResumeTile
              icon={<CalendarCheck size={14} />}
              value={resume.tasks}
              label={t('dialer:tasks.tasks')}
              color="var(--vf-accent)"
            />
          </div>
        </div>

        <div>
          <h3 className="text-[15px] font-semibold mb-3" style={{ color: 'var(--vf-text-1)' }}>
            {t('dialer:tasks.nextDays')}
          </h3>
          <div
            className="rounded-vf-lg border divide-y"
            style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}
          >
            <UpcomingRow label={t('dialer:tasks.tomorrow')} meetings={upcoming.tomorrowMeetings} tasks={upcoming.tomorrowTasks} rdvLabel={t('dialer:tasks.rdvs')} tasksLabel={t('dialer:tasks.tasks')} />
            <UpcomingRow label={t('common:time.thisWeek')} meetings={upcoming.weekMeetings} tasks={upcoming.weekTasks} rdvLabel={t('dialer:tasks.rdvs')} tasksLabel={t('dialer:tasks.tasks')} />
          </div>
        </div>
      </aside>
    </div>
  );
}

interface ResumeTileProps {
  icon: ReactNode;
  value: number;
  label: string;
  color: string;
}

function ResumeTile({ icon, value, label, color }: ResumeTileProps) {
  return (
    <div
      className="rounded-vf-lg border p-3"
      style={{ background: 'var(--vf-surface-2)', borderColor: 'var(--vf-border)' }}
    >
      <div className="flex items-center justify-between mb-2">
        <span style={{ color: 'var(--vf-text-3)' }}>{icon}</span>
      </div>
      <div className="vf-font-mono text-[22px] font-semibold leading-none" style={{ color }}>
        {value}
      </div>
      <div className="text-[11px] mt-1" style={{ color: 'var(--vf-text-3)' }}>
        {label}
      </div>
    </div>
  );
}

interface UpcomingRowProps {
  label: string;
  meetings: number;
  tasks: number;
  rdvLabel: string;
  tasksLabel: string;
}

function UpcomingRow({ label, meetings, tasks, rdvLabel, tasksLabel }: UpcomingRowProps) {
  return (
    <div className="flex items-center justify-between px-3 py-2.5" style={{ borderColor: 'var(--vf-border)' }}>
      <span className="text-[12px]" style={{ color: 'var(--vf-text-2)' }}>
        {label}
      </span>
      <div className="flex items-center gap-3 text-[11px]">
        <span style={{ color: 'var(--vf-text-3)' }}>
          <span className="vf-font-mono font-semibold" style={{ color: 'var(--vf-text-1)' }}>
            {meetings}
          </span>{' '}
          {rdvLabel}
        </span>
        <span style={{ color: 'var(--vf-text-3)' }}>
          <span className="vf-font-mono font-semibold" style={{ color: 'var(--vf-text-1)' }}>
            {tasks}
          </span>{' '}
          {tasksLabel}
        </span>
      </div>
    </div>
  );
}
